import icBox from "@/assets/icons/ic-box.svg?raw";
import icCpuChip from "@/assets/icons/ic-cpu-chip.svg?raw";
import icGrid from "@/assets/icons/ic-grid.svg?raw";
import icLogout from "@/assets/icons/ic-logout.svg?raw";
import icMessageSquare from "@/assets/icons/ic-message-square.svg?raw";
import icTrendUp from "@/assets/icons/ic-trend-up.svg?raw";
import icUser from "@/assets/icons/ic-user.svg?raw";
import icUsers from "@/assets/icons/ic-users.svg?raw";
import { AdminPageTitleProvider } from "@/contexts/admin-page-title-context";
import { Link, Outlet, useLocation, useMatches, useNavigate } from "react-router-dom";
import { useAdminAuthStore } from "@/stores/admin-auth-store";
import { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { usePermissions, type AdminModule } from "@/lib/permissions";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import IconHelp from "@/assets/icons/icon-help.svg";

interface MenuItem {
  path: string;
  label: string;
  icon: string;
  module: AdminModule;
}

const menuItems: MenuItem[] = [
  { path: "/dashboard", label: "대시보드", icon: icGrid, module: "dashboard" },
  { path: "/manage-user", label: "사용자 관리", icon: icUsers, module: "user_management" },
  { path: "/agents", label: "에이전트 관리", icon: icCpuChip, module: "agent_management" },
  { path: "/projects", label: "프로젝트 관리", icon: icBox, module: "project_management" },
  { path: "/inquiries", label: "문의 관리", icon: icMessageSquare, module: "inquiry_management" },
  { path: "/ax-trend-hub", label: "AX Trend Hub", icon: icTrendUp, module: "ax_trend_hub" },
  { path: "/admin-management", label: "관리자 관리", icon: icUser, module: "admin_management" },
];

export const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const matches = useMatches();
  const { logout, admin } = useAdminAuthStore();
  const { can } = usePermissions();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const visibleMenuItems = useMemo(() => menuItems.filter((item) => can.read(item.module)), [can]);

  const isActive = (path: string) =>
    path === "/dashboard" ? location.pathname === "/dashboard" : location.pathname.includes(path);

  const currentMatch = matches[matches.length - 1];
  const routeTitle = (currentMatch?.handle as { title?: string })?.title;
  const activeMenuItem = visibleMenuItems.find((item) => isActive(item.path));
  const pageTitle = routeTitle || activeMenuItem?.label || "대시보드";

  // If the current path is not in the visible menu (e.g. role lost permission),
  // redirect to the first allowed page.
  useEffect(() => {
    if (visibleMenuItems.length === 0) return;
    const onAllowedPath = visibleMenuItems.some((item) => isActive(item.path));
    if (!onAllowedPath) {
      navigate(visibleMenuItems[0].path, { replace: true });
    }
  }, [visibleMenuItems, navigate, location.pathname]);

  const handleLogout = useCallback(async () => {
    // Await logout so the BE call finishes before we tear down the page.
    // Redirecting mid-flight would cancel the request and leave the refresh
    // token active on the server.
    await logout();
    // Hard navigation (not SPA navigate): drops all in-memory JS state and
    // prevents the browser's back-forward cache from restoring this
    // authenticated page on a Back / two-finger swipe (esp. on macOS).
    // `replace` also removes it from history so Back skips past it entirely.
    window.location.replace("/admin/login");
  }, [logout]);

  return (
    <AdminPageTitleProvider>
      <div className="flex h-screen">
        {/* Sidebar */}
        <aside className="w-[288px] bg-white border-r border-[rgba(0,0,0,0.09)] flex flex-col gap-2 pb-6 shrink-0">
          {/* Logo */}
          <div className="border-b  h-[72px] border-[rgba(0,0,0,0.09)] flex items-center py-2 px-6 shrink-0">
            <img src={`${import.meta.env.BASE_URL}svg/pwc-layout.svg`} alt="PwC" />
          </div>

          {/* Menu */}
          <nav className="flex flex-col gap-1 px-3 flex-1">
            {visibleMenuItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-4 p-2 rounded transition-colors outline-none ${
                    active
                      ? "bg-[#FBE9D6] text-[#000000]"
                      : "text-[#000000] hover:bg-[rgba(0,0,0,0.06)]"
                  }`}
                >
                  <span
                    className={`w-6 h-6 shrink-0 [&>svg]:w-6 [&>svg]:h-6 [&_path]:fill-[#000000]`}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.icon) }}
                  />
                  <span
                    className={`text-base flex-1 tracking-[-0.01em] leading-6 ${
                      active ? "font-bold" : "font-normal"
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="px-3">
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="flex items-center gap-4 p-2 rounded w-full bg-[#EBEBEB]  transition-colors"
            >
              <span className="text-base font-normal text-[#000000] flex-1 text-left tracking-[-0.01em] leading-6">
                로그아웃
              </span>
              <span
                className="w-6 h-6 shrink-0 [&>svg]:w-5 [&>svg]:h-5"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(icLogout) }}
              />
            </button>
          </div>
        </aside>

        {/* Logout Confirm Dialog */}
        <ConfirmDialog
          open={showLogoutConfirm}
          onOpenChange={setShowLogoutConfirm}
          iconVariant="neutral"
          icon={<img src={IconHelp} alt="help" />}
          title="로그아웃을 하시겠습니까?"
          description="현재 계정에서 로그아웃됩니다"
          primaryAction={{ label: "네", onClick: handleLogout }}
          secondaryAction={{
            label: "아니요",
            onClick: () => setShowLogoutConfirm(false),
          }}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="h-[72px] border-b border-[rgba(0,0,0,0.09)] flex items-center justify-between px-8 py-2 bg-white shrink-0">
            <span className="text-xl font-bold text-[#000000] leading-7 tracking-[-0.02em]">
              {pageTitle}
            </span>
            {admin?.name && (
              <div className="bg-[#EBEBEB] px-5 py-3 rounded">
                <span className="text-[16px] font-bold text-[#000000]">{admin.name}</span>
              </div>
            )}
          </header>

          {/* Page Content */}
          <main className="flex-1 p-8 bg-white overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </AdminPageTitleProvider>
  );
};
